import React, { Component } from 'react';
import MaterialTable from 'material-table';
import 'components/transactionEditor/RequestTreeWizard.css';
import BlazeInputDropdown, { BlazeDropdownItem } from '../ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import Form from 'react-bootstrap/Form';
import i18n from '../../i18n';
import _ from 'lodash';
import { BodyMatcherItem } from './BodyMatchersList';
import { AttrNode, RequestNodeWidgetType, SelectedElements } from './RequestNodeWidget';
import AttributeNode, { AttributeMatch } from './Wizards/AttributeMatcher/AttributeNode';
import AttributeListPopover from './Wizards/AttributeMatcher/AttributeListPopover';

type BlazeTreeTableProps = {
  sampleBodyJsonUi?: string;
  changeSelection: (id: number, attributeId?: number) => void;
  selectedNodes: SelectedElements[];
  nodeTypes: BlazeDropdownItem[];
  cdataXpathSelected: string | null;
  getMatchTypeFromState: (id: number, defaultOption: BlazeDropdownItem) => BlazeDropdownItem;
  getAttributeMatchTypeFromState: (
    id: number,
    attributeId: number,
    defaultOption: BlazeDropdownItem,
  ) => BlazeDropdownItem;
  matchTypeSelectHandler: (id: number, item: BlazeDropdownItem, cdataXpath: string | null) => void;
  getMatchValueFromState: (id: number, defaultText: string) => string;
  getAttributeMatchValueFromState: (id: number, attributeId: number, defaultText: string) => string;
  matchValueChangeHandler: (id: number, e: any, cdataXpath: string | null) => void;
  onMatchAttributeChangedHandler: (
    id: number,
    attributeId: number,
    attributeMatch: AttributeMatch,
    cdataXpath: string | null,
  ) => void;
  bodyMatcherList: BodyMatcherItem[];
  type: RequestNodeWidgetType;
};

class RequestTreeWizard extends Component<BlazeTreeTableProps> {
  //TODO - it would be better to move this boolean to some external component, but right now
  // it is the only way how to share the pop over state between child and parent popovers
  state = {
    isChildPopoverOpen: false,
  };

  getAttributeNode = (row: any, attr: AttrNode, isInline: boolean) => {
    const foundNode = _.filter(this.props.selectedNodes, sn => sn.id === row.id);
    let attributeSelected = false;
    if (foundNode && foundNode.length > 0) {
      attributeSelected = foundNode[0].attributesId !== undefined && foundNode[0].attributesId.includes(attr.id);
    }

    return (
      <AttributeNode
        isInline={isInline}
        onDropdownOpen={isOpen => {
          this.setState({ isChildPopoverOpen: isOpen });
        }}
        key={attr.name}
        nodeTypes={this.props.nodeTypes}
        onChangeHandler={attributeMatch => {
          //Always update matcher value & selected type
          this.props.onMatchAttributeChangedHandler(row.id, attr.id, attributeMatch, this.props.cdataXpathSelected);

          // Handle selection
          // If attribute is already selected, and we use some matcher, do not reset selection
          if (
            (attributeMatch !== null && attributeSelected === true) ||
            (attributeMatch === null && attributeSelected === false)
          ) {
            return;
          }

          //Toggle selection
          this.props.changeSelection(row.id, attr.id);
        }}
        selectedMatchType={this.props.getAttributeMatchTypeFromState(row.id, attr.id, this.props.nodeTypes[0])}
        selectedMatchValue={this.props.getAttributeMatchValueFromState(row.id, attr.id, attr.value)}
        isSelected={attributeSelected}
        attrName={attr.name}
      />
    );
  };

  getAttributesCell = (row: any) => {
    if (_.isArray(row.attributesNode) && row.attributesNode.length > 0) {
      return (
        <div className={'d-inline-block'}>
          {this.getAttributeNode(row, row.attributesNode[0], true)}

          {/* If there are more elements, show them in the popover */}
          {row.attributesNode.length > 1 && (
            <AttributeListPopover isChildPopoverOpen={this.state.isChildPopoverOpen}>
              {row.attributesNode.map((attr, index) => {
                //Do not include very first item
                if (index == 0) {
                  return;
                }
                return this.getAttributeNode(row, attr, false);
              })}
            </AttributeListPopover>
          )}
        </div>
      );
    }
  };

  getNameCell = (row: any) => {
    if (row.type === 'NODE') {
      return <span className={row.type === 'NODE' ? 'text-primary' : undefined}>{row.name}</span>;
    } else {
      const foundNode = _.filter(this.props.selectedNodes, sn => sn.id === row.id);
      let isSelected = false;
      if (foundNode && foundNode.length > 0) {
        //-1 indicates the node itself has been selected
        isSelected = foundNode[0].attributesId !== undefined && foundNode[0].attributesId.includes(-1);
      }
      return (
        <div className="custom-control custom-checkbox">
          <input
            type={'checkbox'}
            value={row.id}
            name={'leaf-node-select'}
            id={'leaf-' + row.id}
            className={'custom-control-input'}
            onChange={() => this.props.changeSelection(row.id, -1)} // -1 means the node itself (not it's attributes)
            checked={isSelected}
          />
          <label className="custom-control-label cursor-pointer" htmlFor={'leaf-' + row.id}>
            {row.name}
          </label>
        </div>
      );
    }
  };

  getMatchTypeCell = (row: any) => {
    return row.type === 'NODE' ? (
      ''
    ) : (
      <BlazeInputDropdown
        id={'qa-node-match-type-picker'}
        showGroups={false}
        isSearchable={false}
        minDropdownMenuWidth={200}
        maxDropdownHeight={250}
        items={this.props.nodeTypes}
        selected={this.props.getMatchTypeFromState(row.id, this.props.nodeTypes[0])}
        onClick={(item: BlazeDropdownItem) =>
          this.props.matchTypeSelectHandler(row.id, item, this.props.cdataXpathSelected)
        }
        isDefaultOptionSelectable={true}
      />
    );
  };

  getMatchValueCell = (row: any) => {
    return row.type === 'NODE' ? (
      ''
    ) : (
      <Form.Control
        value={this.props.getMatchValueFromState(row.id, row.leafText)}
        title={this.props.getMatchValueFromState(row.id, row.leafText)}
        disabled={this.props.getMatchTypeFromState(row.id, this.props.nodeTypes[0]).name === 'Anything'}
        onChange={(event: any) => {
          this.props.matchValueChangeHandler(row.id, event, this.props.cdataXpathSelected);
        }}
      />
    );
  };

  render() {
    const data: any = this.props.sampleBodyJsonUi ? JSON.parse(this.props.sampleBodyJsonUi) : [];

    return (
      <>
        <div className={'mt-4 position-relative'} style={{ top: 5, zIndex: 5 }}>
          <label className={'font-size-lg font-weight-bold'}>
            {i18n.t('transactions:editor.requestTreeWizard.title')}
          </label>
          <p className={'m-0'}>
            {i18n.t('transactions:editor.requestTreeWizard.description', {
              type: this.props.type === 'json' ? 'JSON path' : 'Xpath',
            })}
          </p>
        </div>
        <div className="blazeTreeTable mt-4">
          <MaterialTable
            localization={{
              toolbar: {
                searchPlaceholder: i18n.t('transactions:editor.searchNodes'),
              },
            }}
            style={{
              padding: '1px',
            }}
            title=""
            data={data}
            columns={[
              {
                title: i18n.t('transactions:editor.nodes'),
                field: 'name',
                cellStyle: {
                  width: '50%',
                },
                render: this.getNameCell,
              },
              {
                title: i18n.t('transactions:editor.attributes'),
                field: 'attributes',
                cellStyle: {
                  width: '20%',
                },
                render: this.getAttributesCell,
              },
              {
                title: i18n.t('transactions:editor.matchType'),
                cellStyle: {
                  width: '15%',
                },
                render: this.getMatchTypeCell,
              },
              {
                title: i18n.t('transactions:editor.matchValue'),
                cellStyle: {
                  width: '15%',
                },
                render: this.getMatchValueCell,
              },
            ]}
            parentChildData={(row, rows) => rows.find(a => a.id === row.parentId)}
            options={{
              selection: false,
              filtering: false,
              paging: false,
              sorting: false,
              debounceInterval: 500,
              defaultExpanded: true,
              searchFieldStyle: {
                border: '1px solid #afbacc',
                paddingLeft: '5px',
                fontSize: '14px',
                width: '100%',
                borderRadius: '3px',
                minWidth: '100%',
              },
              headerStyle: {
                color: '#333840',
                fontSize: '14px',
                fontWeight: 'bold',
                borderTop: '1px solid #c8d1e0',
                borderBottom: '2px solid #c8d1e0',
                width: '23px',
                maxWidth: '23px',
              },
              rowStyle: rowData => ({
                backgroundColor: _.includes(
                  _.map(this.props.selectedNodes, sn => sn.id),
                  rowData.id,
                )
                  ? '#F0F5FC'
                  : '#FFF',
              }),
              draggable: false,
              toolbar: true,
              searchFieldAlignment: 'right',
              showSelectAllCheckbox: false,
              selectionProps: (rowData: any) => ({
                color: 'primary',
                disabled: rowData.type === 'NODE',
              }),
            }}
          />
        </div>
      </>
    );
  }
}

export default RequestTreeWizard;
